import { useEffect, useMemo, useState } from "react"

import type { BaseEventPayload, ElementDragType } from "@atlaskit/pragmatic-drag-and-drop/types"
import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { useUpdateEffect } from "react-use"

import { Option } from "$/models/option"
import { isNullOrUndefined } from "$/utils/gates"
import { getErrorMessages } from "$/utils/get-error-messages"

import { Button, EButtonVariant } from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { GoalsSortSelector } from "@/features/goals-sort-selector/ui/GoalsSortSelector"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { WarningMessage } from "@/shared/WarningMessage"
import PlusIcon from "@/shared/assets/icon/PlusIcon"
import { GoalsSortType } from "@/shared/types/sort"
import Arrow from "@/svgs/Arrow"

import { QueryKey } from "../consts"
import { useOldBeliefApi } from "../hooks/use-old-belief-api"
import { OldBelief } from "../model/types/old-belief"

import { OldBeliefGoalList } from "./OldBeliefGoalList"
import { OldBeliefGoalModal } from "./OldBeliefGoalModal"

import "./styles.scss"

type Props = {
    isToggled: boolean
    toggle: () => void
}

export const OldBeliefGoal = ({ isToggled, toggle }: Props) => {
    const { t }: IUseTranslation = useTranslation()

    const queryClient: QueryClient = useQueryClient()
    const { user }: TUserState = useUser()
    const params = useParams()
    const userId = !isNullOrUndefined(params?.participantId) ? +params?.participantId : user?.id

    const [sort, setSort] = useState<GoalsSortType>("active")
    const [isOpenOldBeliefModal, setIsOpenOldBeliefModal] = useState(false)
    const [currentOldBelief, setCurrentOldBelief] = useState(null)
    const [errorMsg, setErrorMsg] = useState(null)

    const {
        oldBeliefs,
        isLoadingOldBeliefs,
        oldBeliefError,
        archivedOldBeliefs,
        archiveError,
        createOldBelief,
        editOldBelief,
        archiveOldBelief,
        unarchiveOldBelief,
        sortOldBeliefs
    } = useOldBeliefApi({ userId, sort })

    const methods = useForm<{ goals: OldBelief[] }>({
        defaultValues: {
            goals: []
        }
    })

    const { fields, replace, move } = useFieldArray({
        control: methods.control,
        name: "goals",
        keyName: "uuid"
    })

    const hasArchived = useMemo(() => {
        return archivedOldBeliefs?.length > 0
    }, [archivedOldBeliefs])

    useEffect(() => {
        if (!isLoadingOldBeliefs && oldBeliefs) {
            replace(oldBeliefs)
        }
    }, [oldBeliefs, isLoadingOldBeliefs])

    useUpdateEffect(() => {
        if (!hasArchived && sort === "archived") {
            setSort("active")
        }
    }, [hasArchived])

    const onClickOldBelief = (index: number) => {
        setCurrentOldBelief(index)
        setIsOpenOldBeliefModal(true)
    }

    const onSubmitOldBeliefModal = async (values?: Pick<OldBelief, "title" | "description">) => {
        const oldBelief = methods.getValues(`goals.${currentOldBelief}`)
        const fields = methods.getValues("goals")
        const { title, description } = values

        try {
            if (isNullOrUndefined(oldBelief)) {
                await createOldBelief.mutateAsync({
                    title,
                    description,
                    order: fields.length + 1
                })
            } else {
                await editOldBelief.mutateAsync({ order: currentOldBelief + 1, id: oldBelief.id, title, description })
            }
            invalidateQueries()
        } catch (error) {
            console.log(error)
        }

        setCurrentOldBelief(null)
        setIsOpenOldBeliefModal(false)
    }

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: [QueryKey.OldBelief] })
    }

    const onCloseOldBeliefModal = () => {
        setCurrentOldBelief(null)
        setIsOpenOldBeliefModal(false)
    }

    const onChangeSort = (sort: Option) => {
        setSort(sort.value as GoalsSortType)
    }

    const onAddOldBeliefGoal = () => {
        if (oldBeliefError || archiveError) {
            return
        }
        setIsOpenOldBeliefModal(true)
    }

    const DND_AREA_ID: string = "old-belief-goal-drag"

    const onDragEnd = async ({ source, location }: BaseEventPayload<ElementDragType>): Promise<void> => {
        const fromIndex: unknown = source.data.index
        const overIndex: unknown = location.current.dropTargets[0]?.data?.index
        const targetType: unknown = location.current.dropTargets[0]?.data?.type

        if (typeof fromIndex !== "number" || typeof overIndex !== "number") return

        if (fromIndex === overIndex) return

        if (targetType !== DND_AREA_ID) return

        move(fromIndex, overIndex)

        const ids = methods.getValues("goals").map(goal => goal.id)

        try {
            await sortOldBeliefs.mutateAsync({ ids })
            invalidateQueries()
        } catch (error) {
            setErrorMsg(getErrorMessages(error))
        }
    }

    const onArchive = async (index: number) => {
        const { id } = methods.getValues(`goals.${index}`)
        const isArchived = archivedOldBeliefs.includes(id)

        try {
            if (isArchived) {
                await unarchiveOldBelief.mutateAsync(id)
            } else {
                await archiveOldBelief.mutateAsync(id)
            }
            invalidateQueries()
        } catch (error) {
            setErrorMsg(getErrorMessages(error))
        }
    }

    const errorMessage = useMemo(() => {
        if (oldBeliefError || archiveError) {
            const error = oldBeliefError ?? archiveError
            const msg = getErrorMessages(error?.message)?.join() ?? t("An error occurred, please try again.")
            return msg
        }
        return ""
    }, [oldBeliefError, archiveError])

    return (
        <FormProvider {...methods}>
            <div className="d-flex justify-content-between">
                <Button
                    isContainer
                    aria-expanded={isToggled}
                    aria-label={
                        isToggled
                            ? t("participantSide.coachPlanPage.oldBeliefsAside.toggler.collapsed.ariaLabel")
                            : t("participantSide.coachPlanPage.oldBeliefsAside.toggler.collapsed.ariaLabel")
                    }
                    className={buildCssClassName("p-0 m-0", { "btn-old-belief-toggled": isToggled })}
                    onClick={toggle}
                >
                    <div className="old-belief-goal__arrow-btn">
                        <div className="old-belief-goal__arrow-btn_arrows flex">
                            <Arrow />
                            <Arrow />
                        </div>
                    </div>
                </Button>

                {hasArchived && <GoalsSortSelector sort={sort} className="pr-2" onChangeSelect={onChangeSort} />}
            </div>
            <div className="old-belief-goal">
                <p className="color-gray text-center font-extrabold">{t("Old Beliefs")}</p>
                <WarningMessage message={errorMessage} />
                <div className="old-belief-goal-container">
                    {oldBeliefs?.length === 0 && fields.length === 0 ? (
                        <p className="m-0 pl-3 pr-3 color-gray fs-14 font-italic text-center">
                            {t("What old beliefs have been holding you back? What is their significance?")}
                        </p>
                    ) : (
                        <OldBeliefGoalList
                            onArchiveCick={onArchive}
                            archivedBeliefs={archivedOldBeliefs}
                            goals={fields}
                            onClickOldeBelief={onClickOldBelief}
                            onDragEnd={onDragEnd}
                            dragAreaId={DND_AREA_ID}
                        />
                    )}
                </div>
            </div>
            <WarningMessage message={errorMsg} className="mt-3" />
            <div className="d-flex justify-content-center mt-3">
                <Button variant={EButtonVariant.Outlined} onClick={onAddOldBeliefGoal}>
                    <PlusIcon />
                    <span className="d-inline-block ml-2">{t("Add Old Belief")}</span>
                </Button>
            </div>
            {isOpenOldBeliefModal && (
                <OldBeliefGoalModal
                    oldBeliefs={oldBeliefs}
                    oldBeliefIndex={currentOldBelief}
                    isOpen={isOpenOldBeliefModal}
                    onClose={onCloseOldBeliefModal}
                    onSubmit={onSubmitOldBeliefModal}
                />
            )}
        </FormProvider>
    )
}
